import { Button, useNotify, useCreate, useRedirect, SaveButton, Form, SaveHandler, useRefresh } from "react-admin";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { DialogActions } from "@mui/material";
import { singularize } from "inflection";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { getResourceInputs } from "@/utils/helpers/getResourceInputs";
import { getResourceValidation } from "@/utils/validations";
import { AlertType, ResourceName, ValidAny } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface CreateInDialogProps {
  open: boolean;
  onClose: () => void;
  resource: ResourceName;
  disableRedirect?: boolean;
  defaultValues?: Record<string, ValidAny>;
  transform?: (data: ValidAny) => ValidAny;
  /** Called after successful creation with the new record and original (pre-transform) form data */
  onSuccess?: (newRecord: ValidAny, formData: ValidAny) => void | Promise<void>;
}

/**
 * `CreateInDialog` renders a modal dialog for creating a new record
 * for a given resource. It supports generic typing for type-safe form submission,
 * allowing this component to be reused across resources with different data shapes.
 *
 * @param open - Controls whether the dialog is visible
 * @param onClose - Callback invoked when the dialog should be closed
 * @param resource - The name of the resource to create (e.g. ResourceName.USERS, "stoploss-claims/123/items")
 * @param disableRedirect - Controls whether to redirect to Show on success or not (default: false)
 * @param defaultValues - Optional default values to pre-populate the form fields
 * @template T - The shape of the data being submitted via the form
 *
 * @example
 * // Basic usage
 * <CreateInDialog open={true} onClose={handleClose} resource={ResourceName.USERS} />
 *
 * @example
 * // Nested resource with default values
 * <CreateInDialog
 *   open={true}
 *   onClose={handleClose}
 *   resource={`stoploss-claims/${claimId}/items`}
 *   disableRedirect
 *   defaultValues={{ stoploss_claim_id_ref: claimId }}
 * />
 */
export const CreateInDialog = <T extends Record<string, ValidAny> = ValidAny>({
  open,
  onClose,
  resource,
  disableRedirect = false,
  defaultValues,
  transform,
  onSuccess: onSuccessCallback,
}: CreateInDialogProps) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [create] = useCreate();

  // Keep stable refs so stale closures (from Form/useCallback memoising onSubmit) always see the latest values.
  // React Admin's Form can capture the initial onSubmit and never update it, causing all closure values to go stale.
  const onSuccessRef = useRef(onSuccessCallback);
  const transformRef = useRef(transform);
  // Guards against rapid multi-submit (double/triple-click) firing duplicate create
  // mutations — a single Save could otherwise POST N times. See bug #5475.
  const submittingRef = useRef(false);
  useEffect(() => {
    onSuccessRef.current = onSuccessCallback;
  }, [onSuccessCallback]);
  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  // Extract the base resource name for display purposes
  const resourceParts = resource.split("/");
  const baseResource = resourceParts[resourceParts.length - 1];
  const capitalizedResource = baseResource.charAt(0).toUpperCase() + baseResource.slice(1);

  // Resolve the validation function once
  const validate = useMemo(() => {
    if (resource.includes("/items")) {
      return getResourceValidation(ResourceName.CLAIM_ITEMS);
    }
    return getResourceValidation(resource);
  }, [resource]);

  /**
   * Handles the form submission by triggering a `create` mutation.
   *
   * Validation runs here (not via Form's validate/resolver prop) to avoid a
   * react-hook-form timing issue where the resolver can receive stale field
   * values on the first submit attempt, blocking submission even when all
   * fields are correctly filled. By the time onSubmit fires, `data` is
   * guaranteed to reflect the current form state.
   *
   * If validation fails, we return the errors object — React Admin's Form
   * treats a returned object as server-side validation errors, displays them
   * inline, and automatically clears them on the next submit attempt.
   * This avoids the stale-error problem that `setError` with manual type
   * causes (manual errors persist across submits and can block re-submission).
   */
  const handleSubmit = useCallback(
    async (data: T) => {
      // Run form-level validation with the actual submitted data.
      // Returning the errors object lets RA display them inline and clear
      // them automatically on the next submit — no manual setError/clearErrors needed.
      if (validate) {
        const errors = validate(data);
        if (errors && Object.keys(errors).length > 0) {
          return errors;
        }
      }

      // Re-entrancy guard: ignore subsequent submits while one is already in flight.
      if (submittingRef.current) return;
      submittingRef.current = true;

      const transformedData = transformRef.current ? transformRef.current(data) : data;
      try {
        // `returnPromise: true` makes `await` span the full round-trip (the default
        // mutate() form resolves immediately on the next tick, releasing the lock
        // before the request settles — so it would only block a synchronous click
        // burst, not real clicks spaced milliseconds apart). With the promise form
        // the re-entrancy lock below is held for the entire request.
        const newRecord = await create(resource, { data: transformedData }, { returnPromise: true });

        notify(`${singularize(capitalizedResource)} created successfully`, { type: AlertType.SUCCESS });
        onClose();
        if (onSuccessRef.current) await onSuccessRef.current(newRecord, data);
        refresh();
        if (!disableRedirect) redirect(`/${resource}/${newRecord.id}/show`);
      } catch (error) {
        notifyError(error);
      } finally {
        // Release the lock once the mutation settles so a corrected resubmit can proceed.
        submittingRef.current = false;
      }
    },
    [validate, resource, create, notify, notifyError, onClose, refresh, disableRedirect, redirect, capitalizedResource]
  );

  // Get the appropriate inputs based on the base resource
  const getInputs = () => {
    // For nested resources like "stoploss-claims/123/items", use ResourceName.CLAIM_ITEMS inputs
    if (resource.includes("/items")) {
      return getResourceInputs(ResourceName.CLAIM_ITEMS);
    }
    return getResourceInputs(resource);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
        variant="h3"
      >
        Create New{" "}
        {resource.includes(ResourceName.STOPLOSS_CLAIMS) && resource.includes("/items")
          ? "Claim Item"
          : resource === ResourceName.STOPLOSS_CLAIMS
            ? "Stop-Loss Claim"
            : singularize(capitalizedResource)}
      </DialogTitle>

      <Form<T>
        onSubmit={handleSubmit as SaveHandler<T>}
        defaultValues={defaultValues}
        mode="onBlur"
        reValidateMode="onChange"
        noValidate
      >
        <DialogContent dividers sx={{ pb: 10 }}>
          {getInputs()}
        </DialogContent>

        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            px: 3,
            py: 2,
          }}
        >
          <Button size="medium" onClick={onClose} label="Cancel" variant="outlined" />
          {/* alwaysEnable retained: react-admin's default disables Save when the form is
              pristine, which would break the ~15 create dialogs that submit valid
              default values without edits. The submittingRef lock (returnPromise above)
              is the real multi-submit guard, so the button does not need to disable. */}
          <SaveButton size="medium" icon={false} alwaysEnable />
        </DialogActions>
      </Form>
    </Dialog>
  );
};
